<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="field-modal">
      <div class="modal-header">
        <h2 class="text-heading">{{ isEditing ? $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.EDIT_TITLE') : $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.ADD_TITLE') }}</h2>
      </div>
      
      <form @submit.prevent="onSubmit" class="modal-form">
        <!-- Field Name -->
        <div class="form-row">
          <label class="form-label text-heading">{{ $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.FIELD_NAME') }}</label>
          <input
            v-model="formData.field_name"
            type="text"
            class="form-input"
            :class="{ error: fieldNameError }"
            :placeholder="$t('AI_SETTINGS.DATA_COLLECTION.FIELDS.FIELD_NAME_PLACEHOLDER')"
            required
            @input="validateFieldName"
            maxlength="50"
          />
          <p v-if="fieldNameError" class="text-red-600 text-xs mt-1">{{ fieldNameError }}</p>
        </div>
        
        <!-- Display Name -->
        <div class="form-row">
          <label class="form-label text-heading">{{ $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.DISPLAY_NAME') }}</label>
          <input
            v-model="formData.display_name"
            type="text"
            class="form-input"
            :placeholder="$t('AI_SETTINGS.DATA_COLLECTION.FIELDS.DISPLAY_NAME_PLACEHOLDER')"
            required
          />
        </div>
        
        <!-- Description -->
        <div class="form-row">
          <label class="form-label text-heading">{{ $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.DESCRIPTION') }}</label>
          <textarea
            v-model="formData.description"
            rows="3"
            class="form-input"
            :placeholder="$t('AI_SETTINGS.DATA_COLLECTION.FIELDS.DESCRIPTION_PLACEHOLDER')"
          ></textarea>
        </div>
        
        <!-- Required Toggle -->
        <div class="form-row">
          <label class="form-checkbox">
            <input v-model="formData.is_required" type="checkbox" />
            <span class="checkmark"></span>
            <span class="text-heading">{{ $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.IS_REQUIRED') }}</span>
          </label>
        </div>
        
        <!-- Enabled Toggle -->
        <div class="form-row">
          <label class="form-checkbox">
            <input v-model="formData.is_enabled" type="checkbox" />
            <span class="checkmark"></span>
            <span class="text-heading">{{ $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.IS_ENABLED') }}</span>
          </label>
        </div>
        
        <div class="modal-footer">
          <woot-button
            type="button"
            variant="clear"
            @click="onClose"
          >
            {{ $t('AI_SETTINGS.CANCEL') }}
          </woot-button>
          
          <woot-button
            type="submit"
            color-scheme="primary"
            :loading="isSaving"
            :is-disabled="isSubmitDisabled"
          >
            {{ isEditing ? $t('AI_SETTINGS.UPDATE') : $t('AI_SETTINGS.CREATE') }}
          </woot-button>
        </div>
      </form>
    </div>
  </woot-modal>
</template>

<script>
export default {
  name: 'FieldModal',
  
  props: {
    show: {
      type: Boolean,
      default: false
    },
    field: {
      type: Object,
      default: null
    }
  },
  
  emits: ['close', 'save'],
  
  data() {
    return {
      isSaving: false,
      formData: {
        field_name: '',
        display_name: '',
        description: '',
        is_required: false,
        is_enabled: true
      },
      fieldNameError: ''
    }
  },
  
  computed: {
    isEditing() {
      return !!this.field
    },
    isSubmitDisabled() {
      return !!this.fieldNameError || !this.formData.field_name || !this.formData.display_name
    }
  },
  
  watch: {
    field: {
      handler(newField) {
        if (newField) {
          this.formData = { ...newField }
        } else {
          this.resetForm()
        }
      },
      immediate: true
    }
  },
  
  methods: {
    onClose() {
      this.$emit('close')
    },
    
    async onSubmit() {
      if (this.isSaving) return
      
      this.validateFieldName()
      if (this.fieldNameError) return
      
      try {
        this.isSaving = true
        this.$emit('save', { ...this.formData })
      } catch (error) {
        console.error('Error in form submission:', error)
      } finally {
        this.isSaving = false
      }
    },
    
    resetForm() {
      this.formData = {
        field_name: '',
        display_name: '',
        description: '',
        is_required: false,
        is_enabled: true
      }
      this.fieldNameError = ''
    },
    
    validateFieldName() {
      const value = (this.formData.field_name || '').trim()
      if (!value) {
        this.fieldNameError = this.$t('AI_SETTINGS.DATA_COLLECTION.FIELDS.VALIDATION.REQUIRED')
        return
      }
      if (value.length > 50) {
        this.fieldNameError = this.$t('AI_SETTINGS.DATA_COLLECTION.FIELDS.VALIDATION.MAX_LENGTH')
        return
      }
      const pattern = /^[a-z][a-z0-9_]*$/
      if (!pattern.test(value)) {
        this.fieldNameError = this.$t('AI_SETTINGS.DATA_COLLECTION.FIELDS.VALIDATION.PATTERN')
        return
      }
      this.fieldNameError = ''
    }
  }
}
</script>

<style lang="scss" scoped>
.field-modal {
  width: 100%;
  max-width: 600px;
}

.modal-header {
  margin-bottom: var(--space-large);
  
  h2 {
    margin: 0;
    color: var(--color-heading);
    font-size: var(--font-size-large);
  }
}

.modal-form {
  .form-row {
    margin-bottom: var(--space-normal);
    
    .form-label {
      display: block;
      margin-bottom: var(--space-smaller);
      font-weight: var(--font-weight-medium);
      color: var(--color-heading);
    }
    
    .form-input {
      width: 100%;
      padding: var(--space-small);
      border: 1px solid var(--color-border);
      border-radius: var(--border-radius-normal);
      font-size: var(--font-size-default);
      
      &:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: 0 0 0 1px var(--color-primary);
      }

      &.error {
        border-color: var(--color-red);
        box-shadow: 0 0 0 1px var(--color-red);
      }
    }
    
    .form-checkbox {
      display: flex;
      align-items: center;
      cursor: pointer;
      
      input[type="checkbox"] {
        margin-right: var(--space-small);
      }
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-small);
  margin-top: var(--space-large);
  padding-top: var(--space-normal);
  border-top: 1px solid var(--color-border-light);
}

@media (max-width: 768px) {
  .field-modal {
    max-width: 100%;
    margin: var(--space-small);
  }
  
  .modal-footer {
    flex-direction: column-reverse;
    
    .woot-button {
      width: 100%;
    }
  }
}
</style>
